import { useAuth } from "../../context/AuthContext";
import { formatTimestamp } from "../../lib/time";
import { useState } from "react";
import { AlertCircle, KeyRound, Loader2, RefreshCw, ShieldAlert, Trash2 } from "lucide-react";
import { CopyButton } from "../../components/ui/copy-button";
import { Modal } from "../../components/ui/modal";
import { useToast } from "../../components/ui/toast";
import { useApiKeys } from "./use-api-keys";

export function ApiKeysTab() {
  const { user } = useAuth() as any;
  const formatDateTime = (value: string | null) => {
    if (!value) return "Never";
    return formatTimestamp(value, user?.timezone);
  };
  const toast = useToast();
  const {
    keys,
    activeCount,
    maxActiveKeys,
    loading,
    error,
    refresh,
    createKey,
    revokeKey,
  } = useApiKeys();
  const [modalOpen, setModalOpen] = useState(false);
  const [createdSecret, setCreatedSecret] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [revokingKeyId, setRevokingKeyId] = useState<string | null>(null);

  const limitReached = activeCount >= maxActiveKeys;

  const closeModal = () => {
    if (creating) return;
    setModalOpen(false);
    setCreatedSecret(null);
  };

  const handleCreate = async () => {
    setCreating(true);
    try {
      setCreatedSecret(await createKey());
      toast.success("API key created", "Copy the secret now; it will not be shown again.");
    } catch (requestError) {
      toast.danger(requestError instanceof Error ? requestError.message : "Failed to create API key");
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (id: string, name: string) => {
    if (!window.confirm(`Revoke "${name}"? Apps using it will fail immediately.`)) return;
    setRevokingKeyId(id);
    try {
      await revokeKey(id);
      toast.success("API key revoked");
    } catch (requestError) {
      toast.danger(requestError instanceof Error ? requestError.message : "Failed to revoke API key");
    } finally {
      setRevokingKeyId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-ink">API Keys</h2>
          <p className="mt-1 text-xs text-ink-3">{activeCount} of {maxActiveKeys} active keys</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            aria-label="Refresh API keys"
            onClick={() => void refresh()}
            disabled={loading}
            className="rounded-lg border border-line p-2 text-ink-2 transition-colors hover:bg-soft disabled:cursor-not-allowed disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </button>
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            disabled={loading || limitReached}
            className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-hover disabled:cursor-not-allowed disabled:bg-line disabled:text-ink-3"
          >
            Generate New Key
          </button>
        </div>
      </div>

      <p className="flex items-start gap-2 rounded-lg border border-warning/30 bg-warning-soft px-4 py-3 text-sm text-ink-2">
        <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
        Treat keys like passwords. The complete secret is shown once at creation; only its prefix is stored afterward.
      </p>

      {limitReached && (
        <p className="flex items-start gap-2 rounded-lg border border-brand/30 bg-brand-soft px-4 py-3 text-sm text-ink-2">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
          The account has reached its {maxActiveKeys}-key limit. Revoke an existing key before creating another.
        </p>
      )}

      <div className="overflow-hidden rounded-xl shadow-card dark:shadow-card-dark bg-paper-card dark:bg-[#292524]">
        <table className="w-full text-sm">
          <thead className="bg-soft text-ink-3">
            <tr>
              {["Key Name", "Key Prefix", "Created", "Last Used", ""].map((heading, index) => (
                <th key={index} className="px-4 py-3 text-left font-mono text-xs font-semibold uppercase tracking-wider">{heading}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && keys.length === 0 ? (
              <tr className="border-t border-line">
                <td colSpan={5} className="px-4 py-12 text-center text-ink-3">
                  <Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" />
                  Loading API keys…
                </td>
              </tr>
            ) : error ? (
              <tr className="border-t border-line">
                <td colSpan={5} className="px-4 py-10 text-center">
                  <p className="font-semibold text-danger">Could not load API keys</p>
                  <p className="mt-1 text-xs text-ink-3">{error}</p>
                  <button type="button" onClick={() => void refresh()} className="mt-3 rounded-lg border border-line px-3 py-1.5 text-xs font-semibold text-ink hover:bg-soft">Retry</button>
                </td>
              </tr>
            ) : keys.length === 0 ? (
              <tr className="border-t border-line">
                <td colSpan={5} className="px-4 py-12 text-center text-ink-3">
                  <KeyRound className="mx-auto mb-2 h-6 w-6" />
                  No API keys have been created for this account.
                </td>
              </tr>
            ) : keys.map((key) => (
              <tr key={key.id} className="border-t border-line hover:bg-soft/50">
                <td className="px-4 py-3.5 font-semibold text-ink">{key.name}</td>
                <td className="px-4 py-3.5">
                  <code className="rounded bg-soft px-2 py-1 font-mono text-xs text-ink-2">{key.masked}</code>
                </td>
                <td className="px-4 py-3.5 text-ink-2">{formatDateTime(key.createdAt)}</td>
                <td className="px-4 py-3.5 text-ink-2">{formatDateTime(key.lastUsedAt)}</td>
                <td className="px-4 py-3.5 text-right">
                  <button
                    type="button"
                    aria-label={`Revoke ${key.name}`}
                    onClick={() => void handleRevoke(key.id, key.name)}
                    disabled={revokingKeyId === key.id}
                    className="rounded-lg p-2 text-ink-3 transition-colors hover:bg-danger-soft hover:text-danger disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {revokingKeyId === key.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modalOpen && (
        <Modal
          title={createdSecret ? "Key created — copy it now" : "Generate New Key"}
          onClose={closeModal}
          width="max-w-md"
          footer={createdSecret ? (
            <button onClick={closeModal} className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-hover">
              I've stored it securely
            </button>
          ) : (
            <>
              <button
                onClick={closeModal}
                disabled={creating}
                className="rounded-lg border border-line px-4 py-2 text-sm font-semibold text-ink hover:bg-soft disabled:cursor-not-allowed disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => void handleCreate()}
                disabled={creating}
                className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-hover disabled:cursor-not-allowed disabled:bg-line disabled:text-ink-3"
              >
                {creating ? "Creating…" : "Create Key"}
              </button>
            </>
          )}
        >
          {createdSecret ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 rounded-lg border border-success/30 bg-success-soft px-3 py-2.5">
                <code className="flex-1 break-all font-mono text-xs text-ink">{createdSecret}</code>
                <CopyButton value={createdSecret} />
              </div>
              <p className="text-xs text-ink-3">For security, this secret cannot be recovered after you close this dialog.</p>
            </div>
          ) : (
            <p className="text-sm text-ink-2">
              This creates a new developer credential for the current account. The complete secret will be displayed once.
            </p>
          )}
        </Modal>
      )}
    </div>
  );
}
